import { createContext, FC } from 'react';
import { useRouteMatch } from 'react-router';
import { useBookingDetailsQuery } from 'lib/graphql';
import { useSelector } from 'react-redux';
import { getIsSSOAuth } from 'store/authentication/selectors';
import QFFAccessErrorPage from 'pages/QFFAccessErrorPage';
import ErrorPage from 'pages/ErrorPage';
import LoadingPage from 'pages/LoadingPage';
import { useUpdateEffect } from 'react-use';

type ContextValue = ReturnType<typeof useBookingDetailsQuery>;

export const DetailsBookingContext = createContext<ContextValue>({} as any);

const DetailsBookingProvider: FC = ({ children }) => {
  const {
    params: { bookingId },
    path,
  } = useRouteMatch<{ bookingId: string }>();

  const result = useBookingDetailsQuery({ variables: { bookingId }, fetchPolicy: 'cache-and-network' });

  useUpdateEffect(() => {
    result.refetch();
  }, [path]);

  useUpdateEffect(() => {
    if (result.data?.booking.flags.isFinalising) {
      result.startPolling(1500);
    } else {
      result.stopPolling();
    }
  }, [result.data?.booking.flags.isFinalising]);

  const isSSO = useSelector(getIsSSOAuth);

  const { error, loading, data } = result;

  if (error) {
    const isQffForbiddenError = isSSO && error.graphQLErrors?.some((err) => err.extensions?.code === 'FORBIDDEN');
    return isQffForbiddenError ? <QFFAccessErrorPage /> : <ErrorPage />;
  }

  if (loading && !data) {
    return <LoadingPage />;
  }

  return <DetailsBookingContext.Provider value={result}>{children}</DetailsBookingContext.Provider>;
};

export default DetailsBookingProvider;
